import { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Container,
    Grid,
    IconButton,
    InputAdornment,
    Pagination,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { ErrorTryRefetch } from '../../core/ErrorTryRefetch';
import { useBlogPosts } from '../blogHooks';
import { canCreateBlogPost } from '../blogPermissions';
import BlogPostCard from '../components/BlogPostCard';
import BlogFeaturedCard from '../components/BlogFeaturedCard';
import { useGetTags } from '../../hooks/tags';
import { GREEN_700, GREEN_800, cardSx, primaryButtonSx } from '../../core/pvtaStyles';
import PageHeader from '../../core/PageHeader';

const PAGE_SIZE = 10;

const BlogListPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [page, setPage] = useState<number>(1);
    const [search, setSearch] = useState<string>('');
    const [activeTagId, setActiveTagId] = useState<number | null>(null);
    const { data, isLoading, isError, refetch } = useBlogPosts(page, PAGE_SIZE);
    const { tags } = useGetTags();

    if (isError) {
        return <ErrorTryRefetch refetchFunction={() => void refetch()} />;
    }

    const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
    const query = search.toLowerCase().trim();
    const allItems = data?.items ?? [];

    const filteredPosts = allItems.filter((post) => {
        const matchesSearch =
            !query ||
            `${post.title} ${post.teaser} ${post.fallbackText} ${post.author.name}`.toLowerCase().includes(query);
        const matchesTag = activeTagId === null || post.tags.some((tag) => tag.id === activeTagId);
        return matchesSearch && matchesTag;
    });

    const showFeatured = page === 1 && activeTagId === null && query === '' && filteredPosts.length > 0;
    const featuredPost = showFeatured ? filteredPosts[0] : null;
    const gridPosts = showFeatured ? filteredPosts.slice(1) : filteredPosts;

    const openPost = (id: number) => navigate(`/blog/${id}`);

    return (
        <Box sx={{ py: { xs: 4, md: 5 } }}>
            <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>
                {/* Seitenkopf */}
                <PageHeader
                    title="Blog"
                    subtitle="Berichte, Rückblicke und Neuigkeiten aus dem Verbindungsleben."
                    action={canCreateBlogPost() && (
                        isMobile ? (
                            <Tooltip title="Neuer Beitrag">
                                <IconButton onClick={() => navigate('/blog/new')} sx={{ ...primaryButtonSx, px: 0, width: 44, height: 44, flexShrink: 0 }}>
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Button onClick={() => navigate('/blog/new')} startIcon={<AddIcon />} sx={{ ...primaryButtonSx, flexShrink: 0 }}>
                                Neuer Beitrag
                            </Button>
                        )
                    )}
                />

                {/* Toolbar */}
                <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 3 }}>
                    <TextField
                        placeholder="Beiträge suchen..."
                        variant="outlined"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: 'var(--pvta-muted)' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: 'var(--pvta-radius-sm)' },
                            '& .MuiOutlinedInput-input': { py: 1.5 },
                        }}
                    />
                    {tags.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.75 }}>
                            <Chip
                                label="Alle"
                                onClick={() => setActiveTagId(null)}
                                sx={{
                                    borderRadius: 999,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    backgroundColor: activeTagId === null ? GREEN_700 : 'var(--pvta-green-100)',
                                    color: activeTagId === null ? '#ffffff' : 'var(--pvta-text)',
                                    border: '1px solid',
                                    borderColor: activeTagId === null ? GREEN_700 : 'var(--pvta-border)',
                                    '&:hover': { backgroundColor: activeTagId === null ? GREEN_800 : 'var(--pvta-green-100)' },
                                }}
                            />
                            {tags.map((tag) => {
                                const active = activeTagId === tag.id;
                                return (
                                    <Chip
                                        key={tag.id}
                                        label={tag.name}
                                        onClick={() => setActiveTagId(active ? null : tag.id)}
                                        sx={{
                                            borderRadius: 999,
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            backgroundColor: active ? tag.color : 'var(--pvta-green-100)',
                                            color: active ? '#ffffff' : 'var(--pvta-text)',
                                            border: '1px solid',
                                            borderColor: active ? tag.color : 'var(--pvta-border)',
                                            '&:hover': { backgroundColor: active ? tag.color : 'var(--pvta-green-100)' },
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    )}
                </Box>

                {isLoading && (
                    <Stack spacing={3}>
                        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 'var(--pvta-radius-lg)' }} />
                        <Grid container spacing={3}>
                            {[0, 1, 2].map((item) => (
                                <Grid item xs={12} sm={6} md={4} key={item}>
                                    <Skeleton variant="rounded" height={320} sx={{ borderRadius: 'var(--pvta-radius-md)' }} />
                                </Grid>
                            ))}
                        </Grid>
                    </Stack>
                )}

                {!isLoading && data && (
                    <>
                        {featuredPost && (
                            <Box sx={{ mb: 4 }}>
                                <BlogFeaturedCard post={featuredPost} onClick={openPost} />
                            </Box>
                        )}

                        {gridPosts.length > 0 && (
                            <>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--pvta-text)', mb: 2 }}>
                                    Aktuelle Beiträge
                                </Typography>
                                <Grid container spacing={3}>
                                    {gridPosts.map((post) => (
                                        <Grid item xs={12} sm={6} md={4} key={post.id}>
                                            <BlogPostCard post={post} onClick={openPost} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </>
                        )}

                        {filteredPosts.length === 0 && (
                            <Box sx={{ ...cardSx, p: 4, textAlign: 'center' }}>
                                <Typography sx={{ color: 'var(--pvta-muted)' }}>
                                    {allItems.length === 0
                                        ? 'Noch keine Blogbeiträge vorhanden.'
                                        : 'Keine Beiträge für die aktuelle Auswahl gefunden.'}
                                </Typography>
                            </Box>
                        )}

                        {totalPages > 1 && (
                            <Stack direction="row" justifyContent="center" sx={{ mt: 4 }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(_event, nextPage) => setPage(nextPage)}
                                    color="primary"
                                />
                            </Stack>
                        )}
                    </>
                )}
            </Container>
        </Box>
    );
};

export default BlogListPage;
